/**
 * Session Manager
 * Handles session lifecycle, persistence, and attribution
 * UNIFIED session management for both tracker and chatbot widget
 */

import { SessionData } from './types';
import { Storage } from './utils/storage';

export class SessionManager {
  private storage: Storage;
  private session: SessionData | null = null;
  private sessionTimeout: number; // minutes

  constructor(sessionTimeout: number = 30, _debug: boolean = false) {
    this.storage = new Storage();
    this.sessionTimeout = sessionTimeout;
    this.migrateOldKeys();
    this.initSession();
  }

  /**
   * Migrate from old localStorage keys to unified schema
   */
  private migrateOldKeys(): void {
    try {
      // Remove old keys that are no longer used
      const oldKeys = [
        'chatbot_analytics_session',
        'chatbot_analytics_visitor_id',
        'chatbot_analytics_offline_queue'
      ];
      
      for (const key of oldKeys) {
        if (localStorage.getItem(key)) {
          localStorage.removeItem(key);
        }
      }
    } catch (e) {
      // Ignore errors during migration
    }
  }

  /**
   * Initialize or restore session
   */
  private initSession(): void {
    // Try to restore existing session from unified key
    const existingSession = this.storage.getLocal<SessionData>('session');
    
    if (existingSession && this.isSessionValid(existingSession)) {
      this.session = existingSession;
      this.updateLastActivity();
    } else {
      // Create new session
      this.createNewSession();
    }

    // Check for chatbot session in URL (cross-domain tracking)
    this.checkChatbotSessionInUrl();
    
    // Capture UTM parameters
    this.captureUtmParams();
  }

  /**
   * Create a new session
   */
  private createNewSession(): void {
    // Get or create visitor ID (persistent across sessions)
    let visitorId = this.storage.getLocal<string>('visitorId');
    if (!visitorId) {
      visitorId = 'visitor_' + Date.now() + '_' + Math.random().toString(36).slice(2, 11);
      this.storage.setLocal('visitorId', visitorId);
    }
    
    const now = Date.now();
    
    this.session = {
      sessionId: 'session_' + now + '_' + Math.random().toString(36).slice(2, 11),
      visitorId: visitorId!,
      chatbotAssisted: false,
      firstSeen: now,
      lastActivity: now,
      utmParams: {}
    };
    
    this.saveSession();
  }

  /**
   * Check if session is still valid (not expired)
   */
  private isSessionValid(session: SessionData): boolean {
    const lastActivity = session.lastActivity;
    const now = Date.now();
    const timeoutMs = this.sessionTimeout * 60 * 1000;
    
    return (now - lastActivity) < timeoutMs;
  }

  /**
   * Update last activity timestamp
   */
  updateLastActivity(): void {
    if (this.session) {
      this.session.lastActivity = Date.now();
      this.saveSession();
    }
  }

  /**
   * Save session to storage
   */
  private saveSession(): void {
    if (this.session) {
      this.storage.setLocal('session', this.session, this.sessionTimeout);
      
      // Also save in cookies for cross-domain tracking and server-side access
      this.storage.setCookie('sessionId', this.session.sessionId, 1); // 1 day
      this.storage.setCookie('visitorId', this.session.visitorId, 365); // 1 year
    }
  }

  /**
   * Get current session
   */
  getSession(): SessionData | null {
    return this.session;
  }

  /**
   * Get session ID
   */
  getSessionId(): string | null {
    return this.session?.sessionId || null;
  }

  /**
   * Get visitor ID
   */
  getVisitorId(): string | null {
    return this.session?.visitorId || null;
  }

  /**
   * Mark session as chatbot-assisted
   */
  markAsChatbotAssisted(): void {
    if (this.session) {
      this.session.chatbotAssisted = true;
      this.saveSession();
    }
  }

  /**
   * Check if session is chatbot-assisted
   */
  isChatbotAssisted(): boolean {
    return this.session?.chatbotAssisted || false;
  }

  /**
   * Check for chatbot session parameter in URL
   * Used for cross-domain tracking (chatbot widget → e-commerce site)
   */
  private checkChatbotSessionInUrl(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const chatbotSession = urlParams.get('chatbot_session');
    
    if (chatbotSession && this.session) {
      // User came from chatbot interaction
      this.session.chatbotAssisted = true;
      this.saveSession();
      
      // Clean URL (remove parameter)
      this.removeUrlParameter('chatbot_session');
    }
  }

  /**
   * Capture UTM parameters from URL
   */
  private captureUtmParams(): void {
    const urlParams = new URLSearchParams(window.location.search);
    
    const utmParams = {
      source: urlParams.get('utm_source') || undefined,
      medium: urlParams.get('utm_medium') || undefined,
      campaign: urlParams.get('utm_campaign') || undefined,
      term: urlParams.get('utm_term') || undefined,
      content: urlParams.get('utm_content') || undefined
    };

    const gclid = urlParams.get('gclid') || undefined;
    const fbclid = urlParams.get('fbclid') || undefined;

    // Only update if there are new UTM params
    if (Object.values(utmParams).some(val => val !== undefined) || gclid || fbclid) {
      if (this.session) {
        this.session.utmParams = utmParams;
        if (gclid) this.session.gclid = gclid;
        if (fbclid) this.session.fbclid = fbclid;
        this.saveSession();
      }
    }
  }

  /**
   * Remove URL parameter without page reload
   */
  private removeUrlParameter(param: string): void {
    if (window.history && window.history.replaceState) {
      const url = new URL(window.location.href);
      url.searchParams.delete(param);
      window.history.replaceState({}, document.title, url.toString());
    }
  }

  /**
   * Get UTM parameters
   */
  getUtmParams() {
    return this.session?.utmParams || {};
  }

  /**
   * End current session
   */
  endSession(): void {
    if (this.session) {
      this.storage.removeLocal('session');
      this.storage.removeCookie('sessionId');
      this.storage.removeCookie('visitorId');
      this.session = null;
    }
  }

  /**
   * Reset and create new session
   */
  resetSession(): void {
    this.endSession();
    this.createNewSession();
  }
}
